import React, { useMemo } from 'react'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { CaptionMD } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { Offer } from '@/services/PifService'
import { getPeopleImpacted } from '@/services/PifService/PifService'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { formatNumberForDisplay } from '@/utils/numbers'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface TheatricalExpressPerkViewerProps {
  selectedOffer: Offer
  theatricalSlug: string
  projectName: string
  shouldShowDedicationExperiment?: boolean
  earlyAccessMinimumPrice?: number
  isExpressCheckout?: boolean
}

const hasEarlyAccess = (offerPrice: number, earlyAccessMinimumPrice?: number) => {
  if (typeof earlyAccessMinimumPrice !== 'number') return false

  return offerPrice >= earlyAccessMinimumPrice
}

export const TheatricalExpressPerkViewer: React.FC<TheatricalExpressPerkViewerProps> = ({
  selectedOffer,
  earlyAccessMinimumPrice,
  isExpressCheckout,
  theatricalSlug,
  projectName,
}) => {
  const { t } = useTranslate('pif')
  const { tributeOfferIDs } = useProjectMeta()

  const numPeopleImpacted = getPeopleImpacted(selectedOffer)
  const quantity = formatNumberForDisplay({ count: numPeopleImpacted })

  const filteredPerks = useMemo(() => {
    return selectedOffer?.offeredproductSet.filter((o) => {
      if (o?.product?.pif) return false
      if (tributeOfferIDs?.includes(o?.product?.id)) return false
      return true
    })
  }, [selectedOffer?.offeredproductSet, tributeOfferIDs])

  const offerDescription =
    numPeopleImpacted === 0 ? null : (
      <Translate values={{ quantity }} t={t} i18nKey="payitForwardForNPeoplePerkTextV4">
        <span className="font-bold text-core-gray-950">Pay it Forward</span> (at least
        <span className="font-bold text-core-gray-950"> {{ quantity }}</span> movie tickets)
      </Translate>
    )
  return (
    <div className="my-6 flex flex-col gap-2">
      {filteredPerks && (
        <>
          <CaptionMD weight="bold">{t('yourPerksAndImpact', 'Your Perks and Impact')}</CaptionMD>
          {offerDescription && (
            <li className="mb-2 flex lg:items-start">
              <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
              <CaptionMD color="core-gray-700">{offerDescription}</CaptionMD>
            </li>
          )}
          {theatricalSlug === slugs.cabrini && isExpressCheckout && (
            <li className="mb-2 flex items-start lg:items-start">
              <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
              <CaptionMD color="core-gray-700">
                <Translate t={t} i18nKey="exclusiveNewScenePerkV2">
                  <span className="font-bold text-core-gray-950">Exclusive New Scene</span> available after checkout
                </Translate>
              </CaptionMD>
            </li>
          )}
          {hasEarlyAccess(
            getPriceForCurrency(selectedOffer.price, selectedOffer.currency),
            earlyAccessMinimumPrice,
          ) && (
            <li className="mb-2 flex items-center lg:items-start">
              <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
              <CaptionMD color="core-gray-700">
                <Translate t={t} i18nKey="earlyAccessWhenItReleasesInTheApp">
                  <span className="font-bold text-core-gray-950">Early Access</span> when it releases in the app
                </Translate>
              </CaptionMD>
            </li>
          )}
          {filteredPerks.map((perk) => {
            let perkText = perk.name

            if (perk?.product?.pif && perk?.quantity) {
              perkText = t('payItForwardForPeople', {
                count: perk.quantity,
                defaultValue_one: '{{quantity}} person can watch {{projectName}} for free',
                defaultValue_other: '{{quantity}} people can watch {{projectName}} for free',
                projectName,
                quantity: formatNumberForDisplay({ count: perk.quantity, format: '0[.]0a' }),
              })
            }
            return (
              <div key={perk?.id} className="flex items-start gap-2">
                <div className="my-0.5 h-4 w-4">
                  <CircleCheckIcon color1="oxide" color2="white" size={16} />
                </div>
                <CaptionMD className="my-0">{perkText}</CaptionMD>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
